import { useContext, useMemo } from 'react';
import { BrandColorCategory, ColorPaletteType } from '@trinity/chat/enums';
import type { ExclusiveOptionsQuestion } from '@trinity/chat/types/survey';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import type { AppThemingContextValue } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { BrandThemingContext } from '@trinity/chat-components/containers/Branding/Context.js';
import type { BrandThemingContextValue } from '@trinity/chat-components/containers/Branding/interfaces.js';
import * as chart from '../../../contexts/survey-responses/utils/index.js';

// A host may not provide the brand's palettes (the context is then undefined), and a brand may lack a theme or
// palette for a category. Either way the default colors are used rather than the view failing.
const queryOf = (value: BrandThemingContextValue | undefined) => value?.query;

/** The brand's own palette for a category, or undefined when the defaults apply. */
const useBrandPalette = (category: BrandColorCategory) => {
  const query = queryOf(useContext(BrandThemingContext));
  const enabled = useThemingEnabled();
  const theming = query?.data?.theming;

  return useMemo(() => {
    if (!enabled || !theming) return undefined;

    const theme = theming.themes.find(x => x.categoryId === category);
    const palette = theming.palettes.find(x => x.id === theme?.paletteId);

    return palette && palette.typeId !== ColorPaletteType.Preset ? palette : undefined;
  }, [
    category,
    enabled,
    theming,
  ]);
};

export const useOptionsColors = () => {
  const palette = useBrandPalette(BrandColorCategory.Options);

  return useMemo(() => palette?.data.colors ?? lookup[BrandColorCategory.Options], [palette]);
};

const lookup = {
  [BrandColorCategory.Options]: chart.options.OptionsColors,
  [BrandColorCategory.Matrix]: chart.matrix.MatrixColors,
  [BrandColorCategory.Ranking]: {
    extendedStrong: chart.ranking.ExtendedStrongRankingColors,
    extendedWeak: chart.ranking.ExtendedWeakRankingColors,
    strong: chart.ranking.StrongRankingColors,
    weak: chart.ranking.WeakRankingColors,
  },
};

const createRankingColors = (colors: string[]) => {
  const mid = Math.ceil(colors.length / 2);

  const exstrong = colors.slice(0, mid);
  const exweak = colors.slice(mid).reverse();

  const truncs = Math.ceil(exstrong.length * 0.6);
  const truncw = Math.ceil(exweak.length * 0.6);

  const strong = pickDistributedColors(exstrong, truncs);
  const weak = pickDistributedColors(exweak, truncw);

  return {
    extendedStrong: exstrong,
    extendedWeak: exweak,
    weak,
    strong,
  };
};

export const useRankingColors = () => {
  const palette = useBrandPalette(BrandColorCategory.Ranking);

  return useMemo(() => palette ? createRankingColors(palette.data.colors) : lookup[BrandColorCategory.Ranking], [palette]);
};

const createMatrixColors = (colors: string[]) => {
  let i = 0;
  const obj = {};

  while (i < 12) {
    i += 1;

    obj[i] = pickDistributedColors(colors, i);
  }

  return obj;
};

export const useMatrixColors = () => {
  const palette = useBrandPalette(BrandColorCategory.Matrix);

  return useMemo(() => palette ? createMatrixColors(palette.data.colors) : lookup[BrandColorCategory.Matrix], [palette]);
};

export const useExclusiveOptionColors = (settings: ExclusiveOptionsQuestion.Settings) => {
  const colors = useOptionsColors();

  return (opt: ExclusiveOptionsQuestion.Option) => {
    const section = settings.optionSections.find(s => s.identifier === opt.metadata.sectionId);

    // An option whose section the question does not define takes the first color rather than failing the chart.
    return colors[(section?.ordinal ?? 1) - 1];
  };
};

const enabledOf = (value: AppThemingContextValue | undefined) => value?.enabled ?? false;

const useThemingEnabled = () => enabledOf(useContext(AppThemingContext));

function getColorIndices(start: number, end: number, num: number) {
  if (num < 2) return [start];

  const max = num - 1 > end
    ? end + 1
    : num;
  const step = (end - start) / (max - 1);
  const indices: number[] = [];

  for (let i = 0; i < max; i++) {
    // @ts-expect-error arithmetic result passed to parseInt
    const index = parseInt((start + (step * i)));
    indices.push(index);
  }

  return indices;
}

function pickDistributedColors(items: string[], num: number) {
  const end = items.length - 1;
  const indices = getColorIndices(0, end, num);

  return indices.map(x => items[x]);
}
